/**
 * 見た目の個人設定（アクセントカラーと背景画像）。アカウントには保存せず、このブラウザーの
 * localStorageにだけ保存する。ページを描く前に走るスクリプトと設定画面が、同じ確認と反映の
 * 処理を使う。サーバーの部品からも読むので、Reactのフックは use-appearance.ts に置く。
 */
export const APPEARANCE_STORAGE_KEY = "sozo:appearance:v1";

/** 主色の候補。明度はどれも既定の青と同じにし、主色の上の文字のコントラストを保つ。 */
export const ACCENT_COLORS = [
  { id: "blue", label: "ブルー", chroma: 0.207, hue: 259.232 },
  { id: "indigo", label: "インディゴ", chroma: 0.2, hue: 277 },
  { id: "purple", label: "パープル", chroma: 0.2, hue: 303 },
  { id: "pink", label: "ピンク", chroma: 0.19, hue: 354 },
  { id: "red", label: "レッド", chroma: 0.2, hue: 25 },
  { id: "orange", label: "オレンジ", chroma: 0.16, hue: 47 },
  { id: "green", label: "グリーン", chroma: 0.15, hue: 152 },
  { id: "teal", label: "ティール", chroma: 0.11, hue: 195 },
  { id: "graphite", label: "グラファイト", chroma: 0, hue: 0 },
] as const;

export type AccentColorId = (typeof ACCENT_COLORS)[number]["id"];

export const DEFAULT_ACCENT_COLOR: AccentColorId = "blue";

export function accentColorValue(accent: { chroma: number; hue: number }) {
  return `oklch(0.55 ${accent.chroma} ${accent.hue})`;
}

export const BACKGROUND_FITS = [
  { value: "cover", label: "画面を覆う" },
  { value: "contain", label: "全体を表示" },
  { value: "stretch", label: "引き伸ばす" },
  { value: "tile", label: "並べる" },
  { value: "center", label: "中央に置く" },
] as const;

export type BackgroundFit = (typeof BACKGROUND_FITS)[number]["value"];

/**
 * 透明度（%）。大きいほど画像は薄い。画像は画面全体に重ねるので、文字が読めるように
 * 下限を設ける。
 */
export const BACKGROUND_TRANSPARENCY = {
  min: 50,
  max: 95,
  step: 5,
  default: 80,
} as const;

/** ぼかし（px）。 */
export const BACKGROUND_BLUR = {
  min: 0,
  max: 20,
  step: 1,
  default: 0,
} as const;

export type AppearanceBackground = {
  /** 縮小した画像のdata URL。 */
  image: string;
  fit: BackgroundFit;
  transparency: number;
  blur: number;
};

export type AppearanceSettings = {
  accent: AccentColorId;
  background: AppearanceBackground | null;
};

export const DEFAULT_APPEARANCE: AppearanceSettings = {
  accent: DEFAULT_ACCENT_COLOR,
  background: null,
};

type StoredAppearance = {
  accent?: unknown;
  background?: {
    image?: unknown;
    fit?: unknown;
    transparency?: unknown;
    blur?: unknown;
  } | null;
};

type AppearanceRules = {
  accents: Readonly<Record<string, { chroma: number; hue: number }>>;
  defaultAccent: string;
  fits: readonly string[];
  transparency: { min: number; max: number };
  blur: { min: number; max: number };
};

const APPEARANCE_RULES: AppearanceRules = {
  accents: Object.fromEntries(
    ACCENT_COLORS.map((color) => [
      color.id,
      { chroma: color.chroma, hue: color.hue },
    ]),
  ),
  defaultAccent: DEFAULT_ACCENT_COLOR,
  fits: BACKGROUND_FITS.map((fit) => fit.value),
  transparency: BACKGROUND_TRANSPARENCY,
  blur: BACKGROUND_BLUR,
};

/**
 * 保存値を確かめる。既知の色と表示方法、範囲内の数値、画像のdata URLだけを通す。
 * 描画前のスクリプトでも文字列にして使うので、引数のほかに外の値を参照しない。
 */
function parseAppearanceWithRules(
  raw: string | null,
  rules: AppearanceRules,
): AppearanceSettings {
  const fallback = {
    accent: rules.defaultAccent,
    background: null,
  } as AppearanceSettings;
  if (!raw) return fallback;
  let value: StoredAppearance | null = null;
  try {
    value = JSON.parse(raw);
  } catch {
    return fallback;
  }
  if (!value || typeof value !== "object") return fallback;
  const accent =
    typeof value.accent === "string" &&
    Object.hasOwn(rules.accents, value.accent)
      ? value.accent
      : rules.defaultAccent;
  const background = value.background;
  if (
    !background ||
    typeof background !== "object" ||
    typeof background.image !== "string" ||
    !background.image.startsWith("data:image/") ||
    typeof background.fit !== "string" ||
    !rules.fits.includes(background.fit) ||
    typeof background.transparency !== "number" ||
    background.transparency < rules.transparency.min ||
    background.transparency > rules.transparency.max ||
    typeof background.blur !== "number" ||
    background.blur < rules.blur.min ||
    background.blur > rules.blur.max
  ) {
    return { accent, background: null } as AppearanceSettings;
  }
  return {
    accent,
    background: {
      image: background.image,
      fit: background.fit,
      transparency: background.transparency,
      blur: background.blur,
    },
  } as AppearanceSettings;
}

/**
 * 保存値を文書へ反映する。主色は既定の青と同じ明度で、押したときやフォーカスの枠も
 * そろえて差し替える。背景画像は appearance.css が画面全体に重ねて描く。
 * 描画前のスクリプトでも文字列にして使うので、引数のほかに外の値を参照しない。
 */
function applyAppearanceWithRules(
  settings: AppearanceSettings,
  rules: AppearanceRules,
) {
  const root = document.documentElement;
  const accent =
    settings.accent === rules.defaultAccent
      ? undefined
      : rules.accents[settings.accent];
  const accentTokens = {
    "--primary": 0.55,
    "--focus-ring": 0.55,
    "--primary-hover": 0.5,
    "--primary-active": 0.47,
  };
  for (const [token, lightness] of Object.entries(accentTokens)) {
    if (accent) {
      root.style.setProperty(
        token,
        `oklch(${lightness} ${accent.chroma} ${accent.hue})`,
      );
    } else {
      root.style.removeProperty(token);
    }
  }
  if (accent) {
    root.style.setProperty(
      "--primary-foreground",
      `oklch(0.97 ${Math.min(accent.chroma, 0.014)} ${accent.hue})`,
    );
  } else {
    root.style.removeProperty("--primary-foreground");
  }
  const background = settings.background;
  if (background) {
    root.setAttribute("data-app-background", background.fit);
    root.style.setProperty(
      "--app-background-image",
      `url("${background.image}")`,
    );
    root.style.setProperty(
      "--app-background-opacity",
      String((100 - background.transparency) / 100),
    );
    root.style.setProperty("--app-background-blur", `${background.blur}px`);
  } else {
    root.removeAttribute("data-app-background");
    root.style.removeProperty("--app-background-image");
    root.style.removeProperty("--app-background-opacity");
    root.style.removeProperty("--app-background-blur");
  }
}

export function parseAppearance(raw: string | null) {
  return parseAppearanceWithRules(raw, APPEARANCE_RULES);
}

export function applyAppearance(settings: AppearanceSettings) {
  applyAppearanceWithRules(settings, APPEARANCE_RULES);
}

/**
 * ページを描く前に保存値を反映するスクリプト。既定の青や背景のない画面が一瞬出ない
 * ようにする。設定画面と同じ確認と反映の処理を使う。
 */
export function buildAppearanceBootstrapScript() {
  const rules = JSON.stringify(APPEARANCE_RULES);
  return `(function(){try{var rules=${rules};(${applyAppearanceWithRules.toString()})((${parseAppearanceWithRules.toString()})(localStorage.getItem(${JSON.stringify(APPEARANCE_STORAGE_KEY)}),rules),rules)}catch(e){}})()`;
}

/** 背景に使う画像の長辺。画面全体に足りる大きさにし、localStorageに収める。 */
const BACKGROUND_IMAGE_MAX_EDGE = 1920;
/** data URLの文字数の上限。localStorageの容量に余裕を残す。 */
const BACKGROUND_IMAGE_MAX_LENGTH = 3_000_000;

/** 選んだ画像を背景用に縮小してdata URLにする。読めない画像や大きすぎる画像は例外にする。 */
export async function prepareBackgroundImage(file: File) {
  const bitmap = await createImageBitmap(file);
  try {
    const scale = Math.min(
      1,
      BACKGROUND_IMAGE_MAX_EDGE / Math.max(bitmap.width, bitmap.height),
    );
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const context = canvas.getContext("2d");
    if (!context) throw new Error("canvas_unavailable");
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    // WebPで書き出せないブラウザーはPNGを返すので、そのときはJPEGにする。
    const webp = canvas.toDataURL("image/webp", 0.82);
    const dataUrl = webp.startsWith("data:image/webp")
      ? webp
      : canvas.toDataURL("image/jpeg", 0.82);
    if (dataUrl.length > BACKGROUND_IMAGE_MAX_LENGTH) {
      throw new Error("background_image_too_large");
    }
    return dataUrl;
  } finally {
    bitmap.close();
  }
}
